/**
 * @GestionalePD User Profile page UI
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaypal } from '@fortawesome/free-brands-svg-icons';
import {
  Card,
  Stack,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  Avatar,
  Grid,
  FormGroup,
  FormControlLabel,
  TextField,
  CardHeader,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  CircularProgress,
  TableCell,
  CardContent,
  Tooltip,
  Snackbar,
  Alert,
  CardActionArea,
  Divider,
  useMediaQuery,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Autorenew as AutorenewIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  WarningAmber as WarningAmberIcon,
  Verified as VerifiedIcon,
  Info as InfoIcon,
  ContentCopy,
  Check as CheckIcon,
  ShoppingCart,
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  GppGood as GppGoodIcon,
} from '@mui/icons-material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { styled } from '@mui/system';
import { Navigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import html2pdf from 'html2pdf.js';
// -----------------------------------------------------------------------------------------------------------------
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import {
  DefaultOutlinedButton,
  DefaultContainedButton,
  CustomIconButton,
  RedOutlinedButton,
} from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { validatePhoneNumber, getPrice } from '../../utils/numberValidity';
import { PositionStatusLabel } from '../../utils/PositionStatusLabel';
import { ResetPasswordDialog } from '../../utils/ResetPasswordDialog';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { EditPositionDialog } from '../../utils/EditPositionDialog';
import { ThripleTabPanel, a11yProps } from '../../utils/TabStyles';
import { NewPositionDialog } from '../../utils/NewPositionDialog';
import { DecisionDialog } from '../../utils/DecisionDialog';
import { LoadingUI } from '../../utils/LayoutStyles';
import { PaypalCheckout } from '../Checkout';
import { CommonLabel } from '../../utils/Common';
import { DigitalCard } from '../../utils/DigitalCard';
import GenderList from '../../model/gender';
import API from '../../api';

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

/**
 * @returns User profile page UI
 */
export default function ProfilePage() {
  // general
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.selectedTab !== undefined) {
      setValue(location.state.selectedTab);
    }
  }, [location.state]);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditApisRunning, setIsEditApisRunning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [edit, setEdit] = useState(false);
  const userLocalData = JSON.parse(localStorage.getItem('userData'));
  const [internalPosition, setInternalPosition] = useState([]);
  const [externalPosition, setExternalPosition] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState([]);
  const [newCardState, setNewCardState] = useState(false);
  const [hasValidPayedCard, setHasValidPayedCard] = useState(false);
  const [hasDownloadableCard, setHasDownloadableCard] = useState(false);
  const [currentProfile, setCurrentProfile] = useState('');

  // Snackbar handler
  const [onOpenSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    text: null,
    color: 'warning',
  });
  const onSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const [errors, setErrors] = useState({
    name_alias: false,
    gender: false,
    home_country_id: false,
    home_street: false,
    home_zip_code: false,
    home_city_id: false,
    home_city_name: false,
    home_province_id: false,
    home_region_name: false,
    res_country_id: false,
    res_street: false,
    res_zip_code: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_name: false,
  });

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState('noDialog');

  // Show Popup Function
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);

    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}>
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  // Tabs handler
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (value === 1 && cards.length === 0) {
      setIsCardSectionLoading(true);
      getUserCards();
    }
  }, [value]);

  // newsletter subscription dialog
  const [openNewsletterDialog, setOpenNewsletterDialog] = useState(false);

  // open newsletter subscription dialog
  const onOpenNewsletterDialog = () => {
    setOpenNewsletterDialog(true);
  };

  // close newsletter subscription dialog
  const onCloseNewsletterDialog = () => {
    setOpenNewsletterDialog(false);
    setOpen(false);
  };

  // change newsletter permission
  const changeNewsletterPermission = () => {
    API.patch(`users/${userInfo.id}/newsletters`, { allow_news: Number(!allowNews) })
      .then((response) => {
        onCloseNewsletterDialog();
        setAllowNews(!allowNews);

        const userData = JSON.parse(localStorage.getItem('userData')) || {};
        userData.allow_news = Number(!allowNews);
        localStorage.setItem('userData', JSON.stringify(userData));
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // ---------------------------- User details section ---------------------------------
  // Personal Data
  const [name, setName] = useState();
  const [surname, setSurname] = useState();
  const [gender, setGender] = useState();
  const [useAlias, setUseAlias] = useState(false);
  const [aliasName, setAliasName] = useState();
  const [fiscalCode, setFiscalCode] = useState();
  const [email, setEmail] = useState();
  const [birthDate, setBirthDate] = useState();
  const [birthPlace, setBirthPlace] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [callCode, setCallCode] = useState();
  const [userInfo, setUserInfo] = useState({});
  const [resEqualHome, setResEqualHome] = useState();
  const [userPosition, setUserPosition] = useState({});
  const [userEntity, setUserEntity] = useState([]);
  const [isBlacklistedEntity, setIsBlacklistedEntity] = useState(false);

  // api static data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [homeRegionList, setHomeRegionList] = useState([]);
  const [homeCountriesList, setHomeCountriesList] = useState([]);
  const [homeProvincesList, setHomeProvincesList] = useState([]);
  const [homeCitiesList, setHomeCitiesList] = useState([]);

  // Residence
  const [resCountry, setResCountry] = useState({});
  const [resRegion, setResRegion] = useState({});
  const [resProvince, setResProvince] = useState({});
  const [resCity, setResCity] = useState({});
  const [resStreet, setResStreet] = useState('');
  const [resZipCode, setResZipCode] = useState('');
  const [resCityName, setResCityName] = useState(null);
  const [resRegionName, setResRegionName] = useState(null);

  // Permenant address
  const [homeCountry, setHomeCountry] = useState({});
  const [homeRegion, setHomeRegion] = useState({});
  const [homeProvince, setHomeProvince] = useState({});
  const [homeCity, setHomeCity] = useState({});
  const [homeStreet, setHomeStreet] = useState('');
  const [homeZipCode, setHomeZipCode] = useState('');
  const [homeCityName, setHomeCityName] = useState(null);
  const [homeRegionName, setHomeRegionName] = useState(null);

  const [isGdAvailable, setIsGdAvailable] = useState(false);
  const [italy, setItaly] = useState({});

  useEffect(() => {
    if (birthDate) {
      const thirtyYearsAgo = dayjs().subtract(30, 'year');
      const hasLessThan30Years = dayjs(birthDate).isAfter(dayjs(thirtyYearsAgo));
      setIsGdAvailable(hasLessThan30Years);
    }
  }, [birthDate]);

  // Allow newsletter
  const [allowNews, setAllowNews] = useState(Boolean(userInfo.allow_news));

  // handler res equal home
  const onChangeHomePreferences = () => setResEqualHome(!resEqualHome);

  // Close Edit User Data
  const handleCloseEditButtonClick = () => {
    getUserDetails();
    setEdit(!edit);
  };

  // User E-mail contact verification
  const [openVerifyEmailDialog, setOpenVerifyEmailDialog] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [isUserVerifyingEmail, setIsUserVerifyingEmail] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');

  // Open Verify Email Dialog
  const handleOpenVerifyEmailDialog = () => {
    setIsDialogOpen('email');
    setOpenVerifyEmailDialog(true);
  };

  // Close Verify Email Dialog
  const handleCloseVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(false);
    setIsDialogOpen('noDialog');
    setIsUserVerifyingEmail(false);
    setOpen(false);
  };

  // User phone number verification
  const [openVerifyPhoneDialog, setOpenVerifyPhoneDialog] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(false);
  const [isUserVerifyingPhone, setIsUserVerifyingPhone] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');

  // Open Verify Phone Dialog
  const handleOpenVerifyPhoneDialog = () => {
    setIsDialogOpen('phone');
    setOpenVerifyPhoneDialog(true);
    // sendOtpSms()
  };

  // Close Verify Phone Dialog
  const handleCloseVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(false);
    setIsDialogOpen('noDialog');
    setIsUserVerifyingPhone(false);
    setOpen(false);
  };

  // user reset password handler
  const [openResetPassword, setOpenResetPassword] = useState(false);

  // Open reset password model handler
  const handleOpenResetPassword = () => {
    setOpenResetPassword(true);
    setOpen(false);
  };

  // Close reset password model handler
  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
    setOpen(false);
  };

  // userdata snacbar handler
  const {
    open: openEditUserDataSnack,
    handleOpen: handleOpenEditUserDataSnack,
    handleClose: handleCloseEditUserDataSnack,
  } = CreateSnackbarFunctions(false);

  useEffect(() => {
    if (!useAlias) {
      setAliasName(null);
    }
  }, [useAlias]);

  // load current user details
  const getUserDetails = () => {
    API.get(`/users/${userLocalData.id}`)
      .then((response) => {
        // General
        setUserInfo(response.data);

        // Personal Data
        setName(response.data.name);
        setSurname(response.data.surname);
        getDisplayableGender(response.data.gender);
        setAliasName(response.data.name_alias);
        if (response.data.name_alias && response.data.name_alias.length > 0) {
          setUseAlias(true);
        }
        setFiscalCode(response.data.fiscal_code);
        setBirthDate(response.data.birth_date);
        const placeOfBirth =
          response.data.place_of_birth !== null ? response.data.place_of_birth.name : response.data.place_of_birth;
        setBirthPlace(placeOfBirth);
        setEmail(response.data.email);
        setPhoneNumber(response.data.phone);
        setCallCode(response.data.call_code);
        setVerifiedEmail(!!response.data.verified_email);

        // Utils
        setResEqualHome(Boolean(response.data.res_equal_home));
        setHomeZipCode(response.data.home_zip_code);
        setHomeStreet(response.data.home_street);
        setResZipCode(response.data.res_zip_code);
        setResStreet(response.data.res_street);

        // entity data
        const userEntities = response.data.entities;
        const userMasterID = response.data.entity_id;
        const userMasterEntity = userEntities.find((entity) => entity.id === userMasterID);
        setUserEntity(userMasterEntity);
        setIsBlacklistedEntity(response.data.blacklisted_entity);

        // Newsletter preferences
        setAllowNews(Boolean(response.data.allow_news));

        setPositions(response.data.positions);

        // Residence
        setResCountry(response.data.res_country);
        setResRegion(response.data.res_region);
        setResProvince(response.data.res_province);
        setResCity(response.data.res_city);
        setResCityName(response.data.res_city_name);
        setResRegionName(response.data.res_region_name);

        // Permenant address
        setHomeCountry(response.data.home_country);
        setHomeRegion(response.data.home_region);
        setHomeProvince(response.data.home_province);
        setHomeCity(response.data.home_city);
        setHomeCityName(response.data.home_city_name);
        setHomeRegionName(response.data.home_region_name);

        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  const handleNewPosition = () => {
    getUserDetails();
    setUpdatedPositionsCheck(true);
    setUserRatesApiCalled(false);
  };

  const getUserCards = () => {
    API.get(`/memcards/user/${userLocalData.id}`)
      .then((response) => {
        setCards(response.data);

        const hasValidCard = response.data.some((card) => {
          return dayjs(card.campaign.end_date) > dayjs(new Date());
        });

        const hasLastPayedCard = response.data.some((card) => {
          return dayjs(card.campaign.end_date).isAfter(dayjs()) && card.payment_status.type === 'P';
        });

        const hasPayedCard = response.data.some((card) => {
          return card.payment_status.type === 'P' && card.prefix >= '2024';
        });
        
        setHasDownloadableCard(hasPayedCard)
        setHasValidPayedCard(hasLastPayedCard);
        setNewCardState(hasValidCard);
        setIsCardSectionLoading(false);
      })
      .catch((error) => {
        setIsCardSectionLoading(false);
        setIsLoading(false);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // Get address form static data like contries, regions, provines and cities
  const getAddressApi = () => {
    if (resCountriesList.length === 0 || homeCountriesList.length === 0) {
      axios
        .all([API.get('/countries')])
        .then(
          axios.spread((countriesResponse) => {
            setResCountriesList(countriesResponse.data);
            setHomeCountriesList(countriesResponse.data);

            const italyData = countriesResponse.data.find((element) => element.name === 'Italia');
            setItaly(italyData);

            setIsEditApisRunning(false);
            setEdit(true);
          })
        )
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      setIsEditApisRunning(false);
      setEdit(true);
    }
  };

  useEffect(() => {
    if (edit && resCountry && resCountry?.name !== italy?.name) {
      setResProvince('');
      setResRegion('');
      setResCity('');
    }

    if (resCountry?.name !== italy?.name) {
      setResRegionList([]);
      setResProvincesList([]);
      setResCitiesList([]);
    }
  }, [resCountry]);

  useEffect(() => {
    if (edit && homeCountry && homeCountry?.name !== italy?.name) {
      setHomeProvince('');
      setHomeRegion('');
      setHomeCity('');
    }

    if (homeCountry?.name !== italy?.name) {
      setHomeRegionList([]);
      setHomeProvincesList([]);
      setHomeCitiesList([]);
    }
  }, [homeCountry]);

  // Get resRegionList
  useEffect(() => {
    if (edit) {
      if (resCountry !== null && resCountry !== undefined && resCountry !== '') {
        if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0 && resCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: resCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResRegionList(response.data);

              if (resEqualHome) {
                setHomeRegionList(response.data);
              }
            } else {
              setResRegionList([]);
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resCountry, edit]);

  // Get resProvincesList
  useEffect(() => {
    if (edit) {
      if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
        if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0 && resCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: resRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResProvincesList(response.data);

              if (resEqualHome) {
                setHomeProvincesList(response.data);
              }
            } else {
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resRegion, edit]);

  // Get resCitiesList
  useEffect(() => {
    if (edit) {
      if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
        if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: resProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResCitiesList(response.data);
              if (resEqualHome) {
                setHomeCitiesList(response.data);
              }
            } else {
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resProvince, edit]);

  // Get homeRegionList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '') {
        if (typeof homeCountry === 'object' && Object.keys(homeCountry).length > 0 && homeCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: homeCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeRegionList(response.data);
            } else {
              setHomeRegionList([]);
              setHomeProvincesList([]);
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeCountry, edit]);

  // Get homeProvincesList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeRegion !== null && homeRegion !== undefined && homeRegion !== '') {
        if (typeof homeRegion === 'object' && Object.keys(homeRegion).length > 0 && homeCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: homeRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeProvincesList(response.data);
            } else {
              setHomeProvincesList([]);
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeRegion, edit]);

  // Get homeCitiesList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeProvince !== null && homeProvince !== undefined && homeProvince !== '') {
        if (typeof homeProvince === 'object' && Object.keys(homeProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: homeProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeCitiesList(response.data);
            } else {
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeProvince, edit]);

  // Send OTP Eail Verification Code
  const sendOtpEmail = () => {
    setIsUserVerifyingEmail(true);
    API.patch(`/otp/send_mail`, { email: `${email}` })
      .then((response) => {
        if (response.status === 200) {
          setPopUpText('Email inviata con successo');
          showPopup(true);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // verify user e-mail
  const onVerifyUserEmail = () => {
    const otpCheckData = { otp: emailVerificationCode, email };

    API.post(`/checkemailotp`, otpCheckData)
      .then((response) => {
        setPopUpText('Email verificata con successo');

        showPopup(true);
        setVerifiedEmail(true);
        getUserDetails();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Send OTP Sms Verification Code
  const sendOtpSms = () => {
    setIsUserVerifyingPhone(true);
    const countryCode = callCode.slice(1, callCode.length);

    API.patch(`/otp/send_sms?phone=${phoneNumber}&country_code=${countryCode}`)
      .then((response) => {
        setPopUpText('SMS inviato con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const getGenderDBName = (name) => {
    let gender;

    if (name === 'uomo') {
      gender = 'male';
    } else if (name === 'donna') {
      gender = 'female';
    } else if (name === 'altro') {
      gender = 'other';
    } else {
      gender = null;
    }

    return gender;
  };

  // Get Gender Name
  const getDisplayableGender = (gender) => {
    let genderName;

    if (gender === 'male') {
      genderName = 'Uomo';
    } else if (gender === 'female') {
      genderName = 'Donna';
    } else if (gender === 'other') {
      genderName = 'Altro';
    } else {
      genderName = '';
    }

    return setGender({ gender: genderName });
  };

  // verify user phone
  const onVerifyUserPhone = () => {
    API.post(`/checksmsotp?otp=${phoneVerificationCode}&phone=${callCode}${phoneNumber}`)
      .then((response) => {
        setPopUpText('Numero verificato con successo');
        showPopup(true);
        setVerifiedPhone(true);
        getUserDetails();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Update user data request
  const onUpdateUserData = (e) => {
    e.preventDefault();

    if (edit) {
      // user residence data
      const resCityID =
        resCity === null ? null : resCity.id ? resCity.id : resCity === '' ? userInfo.res_city_id : null;

      const _resCityName = typeof resCityName === 'string' ? resCityName : null;

      const resProvinceID =
        resProvince === null
          ? null
          : resProvince.id
          ? resProvince.id
          : resProvince === ''
          ? userInfo.res_province_id
          : null;

      const _resRegionName = typeof resRegionName === 'string' ? resRegionName : null;

      const resCountryID =
        resCountry === null ? null : resCountry.id ? resCountry.id : resCountry === '' ? userInfo.res_country_id : null;

      // Permenant residence data
      const homeCityID = resEqualHome
        ? resCityID
        : !resEqualHome && typeof homeCity.id === 'string' && homeCity.id !== ''
        ? homeCity.id
        : !resEqualHome && typeof homeCity.id === 'string' && homeCity.id === ''
        ? null
        : null;

      const _homeCityName = typeof homeCityName === 'string' ? homeCityName : null;

      const homeProvinceID = resEqualHome
        ? resProvinceID
        : !resEqualHome && homeProvince && typeof homeProvince.id === 'string' && homeCity.id !== ''
        ? homeProvince.id
        : !resEqualHome && homeProvince && typeof homeProvince.id === 'string' && homeProvince.id === ''
        ? null
        : null;

      const _homeRegionName = typeof homeRegionName === 'string' ? homeRegionName : null;

      const homeCountryID = resEqualHome
        ? resCountryID
        : !resEqualHome && homeCountry && typeof homeCountry.id === 'string' && homeCountry.id !== ''
        ? homeCountry.id
        : !resEqualHome && homeCountry && typeof homeCountry.id === 'string' && homeCountry.id === ''
        ? null
        : null;

      const homeStreetValue = resEqualHome ? resStreet : homeStreet;
      const homeZipCodeValue = resEqualHome ? resZipCode : homeZipCode;

      const genderName = getGenderDBName(gender && gender.gender ? gender.gender.toLowerCase() : '');

      // user data object to be modified
      const updatedData = {
        name,
        surname,
        fiscal_code: userInfo.fiscal_code,
        email,
        name_alias: aliasName,
        gender: genderName,
        birth_date: dayjs(birthDate).format('YYYY/MM/DD'),
        place_of_birth: userInfo.place_of_birth.id,
        phone: phoneNumber,
        call_code: callCode,
        res_zip_code: resZipCode,
        res_street: resStreet,
        res_country_id: resCountryID,
        res_equal_home: Number(resEqualHome),
        home_zip_code: homeZipCodeValue,
        home_street: homeStreetValue,
        home_country_id: homeCountryID,
        user_online: userInfo.user_online,
        entity_id: userInfo.entity_id,
        verified: userInfo.verified,
        card_holder: userInfo.card_holder,
        allow_news: Number(allowNews),
        user_roles: userInfo.roles,
      };

      let itemsLeft = false;
      const newErrors = { ...errors };

      Object.keys(updatedData).forEach((key) => {
        const value = updatedData[key];

        if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            if (useAlias || !['name_alias'].includes(key)) {
              itemsLeft = true;
              if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
                newErrors[key] = true;
              }
              handleOpenEditUserDataSnack();
            }
          } else {
            newErrors[key] = false;
          }
        }
      });

      updatedData.res_city_id = typeof _resCityName !== 'string' ? resCityID : null;
      updatedData.res_city_name = _resCityName;
      updatedData.res_province_id = typeof _resRegionName !== 'string' ? resProvinceID : null;
      updatedData.res_region_name = _resRegionName;
      updatedData.home_city_id = resEqualHome ? updatedData.res_city_id : homeCityID;
      updatedData.home_province_id = resEqualHome ? updatedData.res_province_id : homeProvinceID;
      updatedData.home_city_name = resEqualHome ? _resCityName : _homeCityName;
      updatedData.home_region_name = resEqualHome ? _resRegionName : _homeRegionName;

      setErrors(newErrors);

      if (!itemsLeft) {
        API.put(`/users/${userInfo.id}`, updatedData)
          .then((response) => {
            const existingUserData = JSON.parse(localStorage.getItem('userData')) || {};

            const newUserData = {
              birth_date: updatedData.birth_date,
              allow_news: updatedData.allow_news,
              id: userInfo.id,
              card_holder: updatedData.card_holder,
              name: updatedData.name,
              roles: userInfo.roles,
              surname: updatedData.surname,
              fiscal_code: updatedData.fiscal_code,
              verified: updatedData.verified,
            };

            const updatedUserData = {
              ...existingUserData,
              ...newUserData,
            };

            localStorage.setItem('userData', JSON.stringify(updatedUserData));
            setPopUpText('Informazioni salvate');
            showPopup(true);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
        setEdit(false);
      }
    } else {
      setIsEditApisRunning(true);
      getAddressApi();
    }
  };

  // ---------------------------- User Position section ----------------------------------
  // User position internal tab handler
  const [positionTabValue, setPositionTabValue] = useState(0);
  const handleChangePositionTab = (event, newValue) => {
    setPositionTabValue(newValue);

    if (newValue === 0) {
      setSelectedPosition(externalPosition);
    } else {
      setSelectedPosition(internalPosition);
    }
  };

  const [openNewPosition, setOpenNewPosition] = useState(false);

  const handleOpenNewPosition = () => {
    setOpenNewPosition(true);
  };

  const handleCloseNewPosition = () => {
    setOpenNewPosition(false);
    setOpen(false);
  };

  // user position role handler
  const [positions, setPositions] = useState([]);
  const [updatedPositionsCheck, setUpdatedPositionsCheck] = useState(false);
  const [openEditPosition, setOpenEditPosition] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState({});

  // Open Edit Position Modal
  const handleOpenEditPosition = (position) => {
    setOpenEditPosition(true);
    setPositionToEdit(position);
  };

  // Close Edit Position Modal
  const handleCloseEditPosition = () => {
    setOpenEditPosition(false);
    setOpen(false);
  };

  const [openDeletePosition, setOpenDeletePosition] = useState(false);

  const openDeletePositionDialog = (position) => {
    setPositionToEdit(position);
    setOpenDeletePosition(true);
  };

  const closeDeletePositionDialog = () => {
    setPositionToEdit('');
    setOpenDeletePosition(false);
  };

  const deletePosition = (event) => {
    event.preventDefault();

    API.delete(`/charges/${positionToEdit.id}`)
      .then(() => {
        getUserDetails();
        closeDeletePositionDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // ---------------------------- User Membership card section -------------------------
  const [userRates, setUserRates] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState({});
  const [activeCampaignApiCalled, setActiveCampaignApiCalled] = useState(false);
  const [userRatesApiCalled, setUserRatesApiCalled] = useState(false);
  const [totalPrice, setTotalPrice] = useState([]);
  const [disabledStates, setDisabledStates] = useState(userRates.map(() => false));
  const [cards, setCards] = useState([]);
  const [isCardSectionLoading, setIsCardSectionLoading] = useState(false);

  // paypal handler
  const [orderItems, setOrderItems] = useState([]);
  const [isPaying, setIsPaying] = useState(false);
  const [paypalSuccess, setPaypalSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({ select: false, method: null, type: null });
  const [paymentStatus, setPaymentStatus] = useState({});
  const [paymentTypes, setPaymentTypes] = useState({});
  const [paymentTypesApiCalled, setPaymentTypesApiCalled] = useState(false);
  const [subscriptionApiCalled, setSubscriptionApiCalled] = useState(false);
  const [subscriptionApiError, setSubscriptionApiError] = useState('');

  // Membership card handler
  const [openNewCard, setOpenNewCard] = useState(false);
  const [onlineMembershipCard, setOnlineMembershipCard] = useState(true);
  const [membershipCard, setMembershipCard] = useState({
    id: null,
    prefix: null,
    card_number: null,
    campaign_id: null,
    campaign_name: null,
    entity_name: null,
    amount: null,
    currency: null,
    creation_date: null,
    payment_status: null,
    payment_type: null,
  });

  /* ------------------------------- New Card ------------------------------ */
  const [openCreateCardDialog, setOpenCreateCardDialog] = useState(false);

  useEffect(() => {
    const shouldCallSubscriptionApi = isPaying ? paymentTypesApiCalled : userRatesApiCalled && paymentTypesApiCalled;

    if (shouldCallSubscriptionApi) {
      setSubscriptionApiCalled(true);
    }
  }, [isPaying, userRatesApiCalled, paymentTypesApiCalled]);

  // Open New Card Dialog
  const handleOpenCreateCardDialog = () => {
    setIsDialogOpen('createCard');
    setOpenCreateCardDialog(true);
  };

  // Close New Card Dialog
  const handleCloseCreateCardDialog = () => {
    setIsDialogOpen('noDialog');
    setOpenCreateCardDialog(false);
    setOpen(false);
  };

  // Open New Card Modal
  const handleOpenNewCard = (getActiveRates) => {
    setIsDialogOpen('newCard');
    setOpenNewCard(true);
    if (getActiveRates) {
      getMembershipCardRates(userPosition);
    }
    getActiveCampaign();
    getPaymentStatus();
    getPaymentTypes();
  };

  useEffect(() => {
    if (paymentMethod.select && paymentMethod.type === 'BB') {
      if (ibanCode.length === 0) {
        setIsBankDataLoading(true);
      }
      getBankData();
    }
  }, [paymentMethod]);

  // Close New Card Modal
  const handleCloseNewCard = () => {
    setIsDialogOpen('noDialog');
    setOpenNewCard(false);
    setIsPaying(false);
    setPaypalSuccess(false);
    setPaymentMethod({ select: false, method: null, type: null });
    setOpen(false);
    setSubscriptionApiError('');
  };

  // Rates Inputs Disabled
  const handleDisabledInput = (index) => {
    const newDisabledStates = [...disabledStates];
    newDisabledStates[index] = !newDisabledStates[index];
    setDisabledStates(newDisabledStates);
  };

  const [otherValue, setOtherValue] = useState({});

  const isSaveEnabled = !Number.isNaN(totalPrice) && totalPrice > 0;

  // Rates Changes handler
  const handleRateChange = (index, newValue) => {
    setUserRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  // get payment status data
  const getPaymentStatus = () => {
    if (Object.keys(paymentStatus).length === 0) {
      API.get('/paystatus')
        .then((response) => {
          setPaymentStatus(response.data);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // get payment type data
  const getPaymentTypes = () => {
    if (Object.keys(paymentTypes).length === 0) {
      API.get('/paytypes?for_user=1')
        .then((response) => {
          setPaymentTypes(response.data.payment_types);
          setPaymentTypesApiCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    }
  };

  // Get Card Rates
  const getMembershipCardRates = (positionObj) => {
    const apiPosition = Object.keys(positionObj).length > 0 ? positionObj.id : '';

    if (userRates.length === 0 || updatedPositionsCheck) {
      API.get(
        `active_rates/?position_id=${apiPosition}&entity_id=${userEntity.id}&user_birth_date=${dayjs(birthDate).format(
          'YYYY/MM/DD'
        )}`
      )
        .then((response) => {
          const freeOffer = response.data.free_offer;
          const entityRates = response.data.entity_rate.map((obj) => obj.rate);
          const positionRate = response.data.position_rate ? response.data.position_rate.rate : {};

          const isPositionRateEmpty = Object.keys(positionRate).length === 0 && positionRate.constructor === Object;

          const activeRates = freeOffer ? [freeOffer, ...entityRates] : [...entityRates];
          if (!isPositionRateEmpty) {
            activeRates.push(positionRate);
          }

          setUserRates(activeRates);

          setUserRatesApiCalled(true);
          setUpdatedPositionsCheck(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    }
  };

  const getActiveCampaign = () => {
    API.get('/active_campaign')
      .then((response) => {
        setActiveCampaign(response.data);
        setActiveCampaignApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setSubscriptionApiError);
      });
  };

  // Get Rates Total Value
  useEffect(() => {
    let ratesSum;

    if (isGdAvailable) {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setTotalPrice(ratesSum);
  }, [userRates]);

  // Creating new membership card
  const onCreateNewMembershipCard = (e) => {
    e.preventDefault();

    let _amount = 0;
    const items = [];
    const _paypalOrder = [];

    const newCardRates = {
      user_id: userInfo.id,
      campaign_id: userRates[0].campaign_id,
      rates: [],
      payment_entity_id: userEntity.id,
      payment_online: 1,
      digital_format: Number(onlineMembershipCard),
    };

    paymentStatus.forEach((status) => {
      if (status.type === 'W') {
        newCardRates.payment_status = status.id;
      }
    });

    userRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        newCardRates.rates.push({
          id: element.id,
          price: isGdAvailable ? Number(element.price_discounted) : Number(element.price),
        });

        items.push({
          reference_id: element.id,
          amount: {
            currency_code: element.currency,
            value: isGdAvailable ? element.price_discounted : element.price,
          },
          description: element.name,
        });

        if (isGdAvailable) {
          _amount += parseFloat(element.price_discounted);
        } else {
          _amount += parseFloat(element.price);
        }
      }
    });

    if (items.length !== 0) {
      API.post('/memcards', newCardRates)
        .then((response) => {
          setMembershipCard({
            id: response.data.card.id,
            prefix: response.data.card.prefix,
            card_number: response.data.card.card_number,
            campaign_id: response.data.card.campaign.id,
            campaign_name: response.data.card.campaign.description,
            entity_name: response.data.card.payment_entity.name,
            amount: _amount,
            currency: items[0].amount.currency_code,
            creation_date: response.data.card.created_at,
            payment_status: response.data.card.payment_status.id,
            payment_type: null,
          });

          _paypalOrder.push({
            reference_id: response.data.card.card_number,
            amount: {
              currency_code: items[0].amount.currency_code,
              value: _amount,
            },
            description: `Tesseramento PD - ${response.data.card.prefix}${response.data.card.card_number}`,
          });

          setOrderItems(_paypalOrder);
          setIsPaying(true);
          handleCloseCreateCardDialog();
          getUserCards();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      setSnackbarData({ text: "L'importo non può essere zero", color: 'warning' });
      setOpenSnackbar(true);

      setTimeout(() => {
        setOpenSnackbar(false);
      }, POPUP_DURATION_DELAY);
    }
  };

  /* -------------------------------- Bank Data ------------------------------- */
  const [beneficiary, setBeneficiary] = useState('');
  const [ibanCode, setIbanCode] = useState('');
  const [causal, setCausal] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [isBankDataLoading, setIsBankDataLoading] = useState(false);

  // Get Bank API Data
  const getBankData = () => {
    if (beneficiary.length === 0) {
      API.get('/paymentbank')
        .then((response) => {
          const data = response.data[0];
          setBeneficiary(data.intestatario);
          setIbanCode(data.iban);
          setCausal(data.causale);
          setPaymentNote(data.note);
          setIsBankDataLoading(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  const sendBankDataEmail = (e) => {
    e.preventDefault();

    API.post(`/paybanksendmail`, { id: membershipCard.id })
      .then((response) => {
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // operation after concluded payment
  const onPaymentDone = (paymentData) => {
    let payedStatusID;

    paymentStatus.forEach((staus) => {
      if (staus.type === 'P') {
        payedStatusID = staus.id;
      }
    });

    const data = {
      user_id: userInfo.id,
      campaign_id: membershipCard.campaign_id,
      payment_status: payedStatusID,
      payment_type: membershipCard.payment_type,
      payment_entity_id: membershipCard.entity_id,
      payment_online: 1,
    };

    if (paymentData) {
      const date = new Date();
      date.setHours(date.getHours() + 2);
      const newIsoDate = date.toISOString();
      data.payment_date = newIsoDate;
      data.payment_pp_trans_id = paymentData.id;
      data.payment_pp_acc_name = paymentData.name;
      data.payment_pp_acc_surname = paymentData.surname;
      data.payment_pp_acc_mail = paymentData.email;
      data.payment_pp_acc_status = paymentData.status;
      data.payment_pp_acc_id = paymentData.payer_id;
    }

    // In attesa di creazione richiesta
    API.put(`/memcards/${membershipCard.id}`, data)
      .then((response) => {
        getUserCards();
        handleCloseNewCard();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleUserPaying = (card) => {
    let _amount = 0.0;
    let _currency = '';
    const _memcard = {};
    const items = [];

    card.user_rates.forEach((rate) => {
      _currency = rate.currency;
      _amount += parseFloat(rate.price);
    });

    _memcard.id = card.id;
    _memcard.prefix = card.prefix;
    _memcard.card_number = card.card_number;
    _memcard.campaign_id = card.campaign.id;
    _memcard.campaign_name = card.campaign.description;
    _memcard.entity_name = card.payment_entity.name;
    _memcard.entity_id = card.payment_entity.id;
    _memcard.creation_date = card.created_at;
    _memcard.payment_status = card.payment_status.id;
    _memcard.payment_type = card.peyment_type ? card.payment_type.id : null;
    _memcard.currency = _currency;
    _memcard.amount = _amount;

    items.push({
      reference_id: _memcard.card_number,
      amount: {
        currency_code: _memcard.currency,
        value: _memcard.amount,
      },
      description: `Tesseramento PD - ${_memcard.prefix}${_memcard.card_number}`,
    });

    setOrderItems(items);
    setMembershipCard(_memcard);
    handleOpenNewCard(false);
    setIsPaying(true);
  };

  // Get initial user membership card price rates
  useEffect(() => {
    if (!isLoading) {
      if (positions.length > 0) {
        let userHighestPosition = {};
        userHighestPosition = positions.reduce((maxPosition, currentPosition) =>
          currentPosition.level > maxPosition.level ? currentPosition : maxPosition
        );
        setUserPosition(userHighestPosition);
      }

      const internalPos = positions.filter((position) => position.internal === 1);
      const externalPos = positions.filter((position) => position.internal === 0);

      setInternalPosition(internalPos);
      setExternalPosition(externalPos);

      if (positionTabValue === 0) {
        setSelectedPosition(externalPos);
      } else {
        setSelectedPosition(internalPos);
      }
    }
  }, [positions]);

  // Initial user data loading
  useEffect(() => {
    setIsLoading(true);
    getUserDetails();
  }, []);

  const [digitalCardApiCalled, setDigitalCardApiCalled] = useState(false);

  const createDigitalCardPdf = (card) => {
    API.get(`/users/digital_card_data`, {
      params: {
        user_id: userInfo.id,
        campaign_id: card.campaign_id,
      },
    }).then((response) => {
      setCardData({
        campaignPrefix: response.data.campaign_prefix,
        nameAlias: response.data.user_name_alias,
        name: response.data.user_name,
        surname: response.data.user_surname,
        secretary: 'Ufficio Adesioni',
        federation: response.data.user_federation,
        entity: response.data.user_entity,
        code: response.data.card_code,
      });

      setDigitalCardApiCalled(true);
    });
  };

  useEffect(() => {
    if (digitalCardApiCalled) {
      handleGeneratePDF();
    }
  }, [digitalCardApiCalled]);

  const [cardData, setCardData] = useState({
    campaignPrefix: '',
    nameAlias: '',
    name: '',
    surname: '',
    secretary: '',
    federation: '',
    entity: '',
    code: '',
  });

  const handleGeneratePDF = () => {
    const element = document.getElementById('pdf-frame');

    const pageWidthPx = 1016;
    const pageHeightPx = 638;

    const pageWidthMm = pageWidthPx * 0.264583;
    const pageHeightMm = pageHeightPx * 0.264583;

    const options = {
      margin: 0,
      filename: 'digital_card.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: [pageWidthMm, pageHeightMm], orientation: 'landscape' },
    };

    // html2pdf().set(options).from(element).save();

    html2pdf()
      .set(options)
      .from(element)
      .output('blob')
      .then((blob) => {
        const pdfUrl = URL.createObjectURL(blob);

        window.open(pdfUrl, '_blank');
      });

    setDigitalCardApiCalled(false);
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'USER') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Profilo Utente</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ height: '100%' }}>
        <Box width="100%" height="100%">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              allowScrollButtonsMobile
              scrollButtons="auto"
              variant={is600Screen ? 'fullWidth' : 'scrollable'}
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              sx={{ justifyContent: 'space-between' }}
            >
              <Tab label="Anagrafica" {...a11yProps(0, 'thriple')} icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Tesseramento" {...a11yProps(1, 'thriple')} icon={<BadgeIcon />} iconPosition="start" />
              <Tab label="Cariche" {...a11yProps(2, 'thriple')} icon={<GppGoodIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Anagrafiche Utente */}
          <ThripleTabPanel value={value} index={0}>
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <Grid container spacing={3} alignItems="flex-start !important">
                <Grid item xs={12} md={4}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {/* User profile card */}
                      <Card sx={{ p: '24px' }}>
                        <List>
                          <ListItem sx={{ d: 'flex', justifyContent: 'center' }}>
                            <Avatar sx={{ width: '150px', height: '150px' }} src="/assets/images/img/user.png" />
                          </ListItem>

                          <ListItem sx={{ d: 'flex', justifyContent: 'center', pb: '20px' }}>
                            {userInfo.name_alias === null
                              ? `${userInfo.name} ${userInfo.surname}`
                              : userInfo.name_alias}
                          </ListItem>

                          <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.verified ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.verified ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.verified ? 'Verificato' : 'Non Verificato'}
                                </Typography>

                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.card_holder ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.card_holder ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.card_holder ? 'Tesserato' : 'Non Tesserato'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.user_online ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.user_online ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.user_online ? 'Online' : 'Offline'}
                                </Typography>

                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: !userInfo.blocked ? '#dbf6e5' : '#ffe4de',
                                    color: !userInfo.blocked ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {!userInfo.blocked ? 'Attivo' : 'Bloccato'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </ListItem>
                        </List>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ p: '0' }} title="Gestione Comunicazioni" />
                        {/* Comunication preferences */}
                        <FormGroup sx={{ flexDirection: 'column' }}>
                          <FormControlLabel
                            control={<Switch checked={allowNews} onClick={onOpenNewsletterDialog} />}
                            label="Voglio ricevere comunicazioni"
                          />
                        </FormGroup>

                        <DecisionDialog
                          openDialog={openNewsletterDialog}
                          dialogTitle={`${allowNews ? 'Disattiva' : 'Attiva'} comunicazioni`}
                          dialogDescription={`Sei sicuro di voler ${
                            allowNews ? 'disattivare' : 'attivare'
                          } le comunicazioni?`}
                          closeOperation={onCloseNewsletterDialog}
                          requestOperation={changeNewsletterPermission}
                          requestText={allowNews ? 'Disattiva' : 'Attiva'}
                          closeText={'Chiudi'}
                          RequestButton={DefaultContainedButton}
                        />
                      </Card>
                    </Grid>

                    {/* Modify password section */}
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ p: '0' }} title="Gestione Password" />
                        <DefaultContainedButton onClick={handleOpenResetPassword}>
                          Modifica Password
                        </DefaultContainedButton>
                      </Card>

                      <ResetPasswordDialog openDialog={openResetPassword} closeDialog={handleCloseResetPassword} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* User information section */}
                <Grid item xs={12} md={8}>
                  <Card sx={{ p: '24px', minHeight: '764px' }}>
                    <form onSubmit={onUpdateUserData}>
                      {!isEditApisRunning ? (
                        <Grid container spacing={3} justifyContent="start !important">
                          {/* Peronal Title */}
                          <Grid item xs={12}>
                            <h4>Informazioni Personali</h4>
                          </Grid>

                          {/* Name */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={name} label="Nome" />
                          </Grid>

                          {/* Surname */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={surname} label="Cognome" />
                          </Grid>

                          {/* Alias */}
                          <Grid item xs={12} md={useAlias ? 6 : 12} sx={{ height: '80px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={!edit}
                                  checked={useAlias}
                                  onChange={(e) => {
                                    setUseAlias(e.target.checked);
                                  }}
                                />
                              }
                              label="Utilizza nome alias"
                            />
                          </Grid>
                          {useAlias && (
                            <>
                              {/* name alias */}
                              <Grid item xs={12} md={6}>
                                <CustomTextField
                                  error={errors.name_alias}
                                  disabled={!edit}
                                  onChange={(e) => {
                                    setAliasName(e.currentTarget.value);
                                  }}
                                  value={aliasName}
                                  label="Alias"
                                />
                              </Grid>
                            </>
                          )}

                          {/* gender */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              options={GenderList}
                              disabled={!edit}
                              getOptionLabel={(option) => option.gender}
                              isOptionEqualToValue={(option, value) => option.gender === value.gender}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.gender}>
                                  {option.gender}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                setGender(value);
                              }}
                              defaultValue={gender}
                              renderInput={(params) => (
                                <TextField {...params} label="Sesso" error={errors.gender} variant="outlined" />
                              )}
                            />
                          </Grid>

                          {/* Birth Date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker required disabled label="Data di Nascita" value={dayjs(birthDate)} />
                            </LocalizationProvider>
                          </Grid>

                          {/* Fiscal Code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={birthPlace} label="Luogo di Nascita" />
                          </Grid>

                          {/* Fiscal Code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={fiscalCode} label="Codice Fiscale" />
                          </Grid>

                          {/* Circolo */}
                          <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                            <CustomTextField
                              disabled
                              defaultValue={userEntity ? userEntity.name : ''}
                              label="Circolo"
                            />
                          </Grid>

                          {/* User phone number call code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={callCode} label="Prefisso" />
                          </Grid>

                          {/* Phone */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              disabled
                              defaultValue={phoneNumber}
                              label="Cellulare"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_phone ? (
                                      <Tooltip arrow title="Numero verificato" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Numero non verificato" placement="top">
                                        <WarningAmberIcon
                                          onClick={handleOpenVerifyPhoneDialog}
                                          sx={{ color: 'orange !important', cursor: 'pointer' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Phone number verification dialog */}
                            <Dialog fullWidth open={openVerifyPhoneDialog} onClose={() => {}}>
                              <DialogTitle>Verifica Numero</DialogTitle>

                              <DialogContent>
                                {/* Phone Verification */}
                                <Stack
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="12px"
                                  id="register-email-token-container"
                                >
                                  <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                    {/* Phone Number */}
                                    <Stack
                                      direction={is600Screen ? 'row' : 'column'}
                                      justifyContent="center"
                                      alignItems="center"
                                      gap="6px"
                                    >
                                      <Typography variant="h6">Numero di telefono:</Typography>
                                      <Typography variant="span">
                                        {callCode}
                                        {phoneNumber}
                                      </Typography>
                                      {isUserVerifyingPhone && !verifiedPhone && (
                                        <Tooltip title="In attesa di verifica" placement="right">
                                          <AutorenewIcon sx={{ color: '#414670' }} />
                                        </Tooltip>
                                      )}

                                      {verifiedPhone && (
                                        <Tooltip title="Numero verificato" placement="right">
                                          <CheckIcon sx={{ color: 'green' }} />
                                        </Tooltip>
                                      )}
                                    </Stack>

                                    {isUserVerifyingPhone && !verifiedPhone && (
                                      <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                        {/* Input & Submit Button */}
                                        <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                          <CustomTextField
                                            size="small"
                                            inputProps={{ maxLength: 6 }}
                                            onChange={(e) => validatePhoneNumber(e, setPhoneVerificationCode)}
                                            label="Inserisci il codice"
                                          />
                                          <DefaultOutlinedButton
                                            disabled={verifiedPhone}
                                            onClick={() => onVerifyUserPhone()}
                                          >
                                            Verifica
                                          </DefaultOutlinedButton>
                                        </Stack>

                                        {/* Resend SMS */}
                                        <Box sx={{ textAlign: 'center' }}>
                                          <Typography>Non hai ricevuto l'sms?</Typography>
                                          <Typography
                                            sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                            onClick={() => sendOtpSms()}
                                          >
                                            Invialo di nuovo
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    )}
                                    {!verifiedPhone && !isUserVerifyingPhone && (
                                      <DefaultOutlinedButton onClick={() => sendOtpSms()}>
                                        Verifica Numero
                                      </DefaultOutlinedButton>
                                    )}
                                  </Stack>
                                </Stack>
                                {isDialogOpen === 'phone' && displayPopup()}
                              </DialogContent>
                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyPhoneDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* User Email Verification */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              disabled
                              defaultValue={email}
                              label="Email"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_email ? (
                                      <Tooltip arrow title="Email verificata" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Email non verificata" placement="top">
                                        <WarningAmberIcon
                                          onClick={handleOpenVerifyEmailDialog}
                                          sx={{ color: 'orange !important', cursor: 'pointer' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Email verification dialog */}
                            <Dialog fullWidth open={openVerifyEmailDialog} onClose={() => {}}>
                              <DialogTitle>Verifica E-mail</DialogTitle>

                              <DialogContent>
                                <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                  {/* Email Address */}
                                  <Stack
                                    direction={is600Screen ? 'row' : 'column'}
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="6px"
                                  >
                                    <Typography variant="h6">E-mail:</Typography>
                                    <Typography variant="span">{email}</Typography>
                                    {isUserVerifyingEmail && !verifiedEmail && (
                                      <Tooltip title="In attesa di verifica" placement="right">
                                        <AutorenewIcon sx={{ color: '#414670' }} />
                                      </Tooltip>
                                    )}
                                    {verifiedEmail && (
                                      <Tooltip title="Email verificata" placement="right">
                                        <CheckIcon sx={{ color: 'green' }} />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  {!verifiedEmail && isUserVerifyingEmail && (
                                    <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                      {/* Input & Submit Button */}
                                      <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                        <CustomTextField
                                          size="small"
                                          inputProps={{ maxLength: 6 }}
                                          onChange={(e) => validatePhoneNumber(e, setEmailVerificationCode)}
                                          type="tel"
                                          label="Inserisci il codice"
                                        />
                                        <DefaultOutlinedButton onClick={() => onVerifyUserEmail()}>
                                          Verifica
                                        </DefaultOutlinedButton>
                                      </Stack>

                                      {/* Resend Email verifcation code */}
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography>Non hai ricevuto l'email?</Typography>
                                        <Typography
                                          sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                          onClick={() => sendOtpEmail()}
                                        >
                                          Inviala di nuovo
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  )}

                                  {!verifiedEmail && !isUserVerifyingEmail && (
                                    <DefaultOutlinedButton onClick={() => sendOtpEmail()}>
                                      Verifica Email
                                    </DefaultOutlinedButton>
                                  )}
                                </Stack>
                                {isDialogOpen === 'email' && displayPopup()}
                              </DialogContent>

                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyEmailDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* User residence address section */}
                          <Grid item xs={12}>
                            <h4>Informazioni di Residenza</h4>
                          </Grid>

                          {/* Residence address Country */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-country"
                              disabled={!edit}
                              options={
                                resCountry?.name === italy.name
                                  ? resCountriesList.filter(
                                      (country) =>
                                        (!resRegion || country.id === resRegion.country_id) &&
                                        (!resProvince || country.id === resProvince.country_id)
                                    )
                                  : resCountriesList
                              }
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => option.name}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              value={resCountry}
                              onChange={(e, value) => setResCountry(value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Paese *"
                                  error={errors.res_country_id}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Region */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-region"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resRegionList.filter(
                                (region) =>
                                  (!resCountry || region.country_id === resCountry.id) &&
                                  (!resProvince || region.id === resProvince.region_id)
                              )}
                              isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.region_name;
                              }}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.region_name}>
                                  {option.region_name}
                                </Box>
                              )}
                              value={
                                resRegionName === undefined ||
                                resRegionName === null ||
                                resCountry?.name === italy?.name
                                  ? resRegion
                                  : resRegionName
                              }
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResRegion(value);
                                } else if (value && value.inputValue) {
                                  setResRegion(value.inputValue);
                                } else {
                                  setResRegion(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if(resCountry?.name !== italy?.name){
                                  setResRegionName(value);
                                }else{
                                  setResRegionName(null)
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Regione *"
                                  error={errors.res_region_name}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Province */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-province"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit || (resCountry && resCountry?.name !== italy.name)}
                              options={resProvincesList.filter(
                                (province) =>
                                  (!resRegion || province.region_name === resRegion.region_name) &&
                                  (!resCity || province.id === resCity.province_id) &&
                                  (!resCountry || province.country_id === resCountry.id)
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={resProvince}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResProvince(value);
                                } else if (value && value.inputValue) {
                                  setResProvince(value.inputValue);
                                } else {
                                  setResProvince(value);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Provincia ${resCountry?.name === italy.name ? '*' : ''}`}
                                  variant="outlined"
                                  error={errors.res_province_id}
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address City */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-city"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resCitiesList.filter(
                                (city) => !resProvince || city.province_id === resProvince.id
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={
                                resCityName === undefined || resCityName === null || resCountry?.name === italy?.name
                                  ? resCity
                                  : resCityName
                              }
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResCity(value);
                                } else if (value && value.inputValue) {
                                  setResCity(value.inputValue);
                                } else {
                                  setResCity(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if(resCountry?.name !== italy?.name){
                                  setResCityName(value);
                                }else{
                                  setResCityName(null)
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Comune *"
                                  variant="outlined"
                                  error={errors.res_city_id || errors.res_city_name}
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Street */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-street"
                              disabled={!edit}
                              error={errors.res_street}
                              fullWidth
                              name="resStreet"
                              label="Indirizzo di Residenza *"
                              value={resStreet}
                              onChange={(e) => setResStreet(e.target.value)}
                              inputProps={{
                                maxLength: 50,
                              }}
                            />
                          </Grid>

                          {/* Residence address postal code */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-zipcode"
                              disabled={!edit}
                              error={errors.res_zip_code}
                              fullWidth
                              label="CAP di Residenza *"
                              name="resZipCode"
                              onChange={(e) => setResZipCode(e.target.value)}
                              value={resZipCode}
                              inputProps={{
                                maxLength: 15,
                              }}
                            />
                          </Grid>

                          {/* User permenant address */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="primary"
                                  checked={resEqualHome}
                                  onChange={onChangeHomePreferences}
                                  disabled={!edit}
                                />
                              }
                              label="Le informazioni di Domicilio corrispondono a quelle di Residenza"
                            />
                          </Grid>

                          {!resEqualHome && (
                            <Grid container spacing={3} sx={{ margin: '0' }}>
                              {/* Permenant address Title */}
                              <Grid item xs={12}>
                                <h4>Informazioni di Domicilio</h4>
                              </Grid>

                              {/* Permenant address Country */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-country"
                                  disabled={!edit}
                                  options={
                                    homeCountry?.name === italy.name
                                      ? homeCountriesList.filter(
                                          (country) =>
                                            (!homeRegion || country.id === homeRegion.country_id) &&
                                            (!homeProvince || country.id === homeProvince.country_id)
                                        )
                                      : homeCountriesList
                                  }
                                  getOptionLabel={(option) => option.name || ''}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={homeCountry}
                                  onChange={(e, value) => setHomeCountry(value)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Paese *"
                                      error={errors.home_country_id}
                                      variant="outlined"
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Region */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-region"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={homeRegionList.filter(
                                    (region) =>
                                      (!homeCountry || region.country_id === homeCountry.id) &&
                                      (!homeProvince || region.region_name === homeProvince.region_name)
                                  )}
                                  isOptionEqualToValue={(option, value) => value.region_name === option.region_name}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.region_name || '';
                                  }}
                                  value={
                                    homeRegionName !== null &&
                                    homeRegionName !== undefined &&
                                    homeCountry?.name !== italy?.name
                                      ? homeRegionName
                                      : homeRegion
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.region_name}>
                                      {option.region_name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeRegion(value);
                                    } else if (value && value.inputValue) {
                                      setHomeRegion(value.inputValue);
                                    } else {
                                      setHomeRegion(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if(homeCountry?.name !== italy?.name){
                                      setHomeRegionName(value);
                                    }else{
                                      setHomeRegionName(null)
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Regione *"
                                      variant="outlined"
                                      error={errors.home_region_name}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Province */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-province"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit || (homeCountry && homeCountry?.name !== italy.name)}
                                  options={homeProvincesList.filter(
                                    (province) =>
                                      (!homeRegion || province.region_name === homeRegion.region_name) &&
                                      (!homeCity || province.id === homeCity.province_id)
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={homeProvince}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeProvince(value);
                                    } else if (value && value.inputValue) {
                                      setHomeProvince(value.inputValue);
                                    } else {
                                      setHomeProvince(value);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={`Provincia ${homeCountry?.name === italy.name ? '*' : ''}`}
                                      variant="outlined"
                                      error={errors.home_province_id}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address City */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-city"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={homeCitiesList.filter(
                                    (city) => !homeProvince || city.province_id === homeProvince.id
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={
                                    homeCityName !== null &&
                                    homeCityName !== undefined &&
                                    homeCountry?.name !== italy?.name
                                      ? homeCityName
                                      : homeCity
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeCity(value);
                                    } else if (value && value.inputValue) {
                                      setHomeCity(value.inputValue);
                                    } else {
                                      setHomeCity(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if(homeCountry?.name !== italy?.name){
                                      setHomeCityName(value);
                                    }else{
                                      setHomeCityName(null)
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Comune *"
                                      variant="outlined"
                                      error={errors.home_city_id || errors.home_city_name}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Street */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="home-street"
                                  error={errors.home_street}
                                  disabled={!edit}
                                  fullWidth
                                  label="Indirizzo di Domicilio *"
                                  onChange={(e) => setHomeStreet(e.target.value)}
                                  defaultValue={homeStreet}
                                  inputProps={{
                                    maxLength: 50,
                                  }}
                                />
                              </Grid>

                              {/* Permenant address postal code */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="home-zipcode"
                                  disabled={!edit}
                                  error={errors.home_zip_code}
                                  fullWidth
                                  label="CAP di Domicilio *"
                                  onChange={(e) => setHomeZipCode(e.currentTarget.value)}
                                  defaultValue={homeZipCode}
                                  inputProps={{
                                    maxLength: 15,
                                  }}
                                />
                              </Grid>
                            </Grid>
                          )}

                          {/* User details footer section */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                              {edit && (
                                <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>
                                  Annulla
                                </DefaultOutlinedButton>
                              )}

                              <DefaultContainedButton type="submit">
                                {edit ? 'Salva' : 'Modifica'}
                              </DefaultContainedButton>

                              <CustomSnackbar
                                open={openEditUserDataSnack}
                                handleClose={handleCloseEditUserDataSnack}
                                severity="warning"
                                message="Inserisci i dati mancanti!"
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ minHeight: resEqualHome ? '764px' : '1052px' }}
                        >
                          <CircularProgress />
                        </Stack>
                      )}
                    </form>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              ErrorUI(errorMessage)
            )}
          </ThripleTabPanel>

          {/* User membership card section */}
          <ThripleTabPanel value={value} index={1}>
            {isLoading || isCardSectionLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                <Stack direction="row" justifyContent="end" gap="12px">
                  <div style={{ display: 'none' }}>
                    <DigitalCard {...cardData} />
                  </div>

                  <DefaultContainedButton
                    onClick={() => handleOpenNewCard(true)}
                    disabled={newCardState || isBlacklistedEntity}
                    startIcon={<AddIcon />}
                  >
                    Crea Tessera
                  </DefaultContainedButton>
                </Stack>
                {/* Checkout dialog for payment */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: '0 0 24px' }}>
                  <Dialog fullWidth open={openNewCard} onClose={() => {}}>
                    <CardHeader
                      sx={{ paddingTop: '0' }}
                      avatar={
                        <IconButton
                          edge="start"
                          color="inherit"
                          sx={{ display: paymentMethod.select ? 'flex' : 'none' }}
                          onClick={() => setPaymentMethod({ select: false, method: null, type: null })}
                          aria-label="back"
                        >
                          <ArrowBackIosNewIcon />
                        </IconButton>
                      }
                    />

                    {subscriptionApiCalled && subscriptionApiError.length === 0 ? (
                      <form>
                        <DialogContent>
                          {!isPaying && !paypalSuccess && !paymentMethod.select ? (
                            <Grid container spacing={3}>
                              <Grid item xs={12} sx={{ overflow: 'auto' }}>
                                {userRates.length > 0 ? (
                                  <>
                                    {activeCampaign?.description && (
                                      <DialogTitle>{`Tesseramento ${activeCampaign.description}`}</DialogTitle>
                                    )}

                                    <Alert severity="error" variant="filled" sx={{ marginBottom: '8px' }}>
                                      Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a,
                                      presidente, parlamentare) inserisci la tua carica nella relativa sezione
                                      (“cariche”) prima di procedere al tesseramento.{' '}
                                    </Alert>

                                    <TableContainer>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell align="center">Tariffa</TableCell>
                                            <TableCell align="right">Costo</TableCell>
                                            <TableCell align="center">Valuta</TableCell>
                                            <TableCell align="center">Modificabile</TableCell>
                                          </TableRow>
                                        </TableHead>

                                        <TableBody>
                                          {userRates.map((rate, index) => (
                                            <TableRow hover key={rate.id}>
                                              <TableCell align="center">{rate.name}</TableCell>
                                              {/* <TableCell sx={{ minWidth: '60px' }} align="right">
                                                {disabledStates[index] ? (
                                                  <CustomTextField
                                                    select
                                                    onChange={(e) => handleRateChange(index, e.target.value)}
                                                    label="Importo"
                                                  >
                                                    <MenuItem value={0}>0</MenuItem>
                                                    <MenuItem value={5}>5</MenuItem>
                                                    <MenuItem value={10}>10</MenuItem>
                                                    <MenuItem value={20}>20</MenuItem>
                                                    <MenuItem value={50}>50</MenuItem>
                                                    <MenuItem value={100}>100</MenuItem>
                                                    <MenuItem value={200}>200</MenuItem>
                                                    <MenuItem value={500}>500</MenuItem>

                                                  </CustomTextField>
                                                ) : (
                                                  // Se l'elemento è in modalità visualizzazione
                                                  <Typography>
                                                    {isGdAvailable ? rate.price_discounted : rate.price}
                                                  </Typography>
                                                )}
                                              </TableCell> */}
                                              <TableCell sx={{ minWidth: '40px' }} align="right">
                                                {disabledStates[index] ? (
                                                  otherValue[index]?.isOther ? (
                                                    <CustomTextField
                                                      value={otherValue[index]?.value || ''}
                                                      onKeyDown={(e) => {
                                                        const allowedKeys = ['Backspace', 'Tab', '.', ','];
                                                        if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                                                          e.preventDefault();
                                                        }
                                                      }}
                                                      onChange={(e) => {
                                                        const inputValue = e.target.value.replace(',', '.');
                                                        setOtherValue({
                                                          ...otherValue,
                                                          [index]: {
                                                            ...otherValue[index],
                                                            value: inputValue,
                                                          },
                                                        });
                                                        handleRateChange(index, inputValue);
                                                      }}
                                                      label="Importo personalizzato"
                                                    />
                                                  ) : (
                                                    <CustomTextField
                                                      customStyles={{ width: '100px' }}
                                                      select
                                                      onChange={(e) => {
                                                        const selectedValue = e.target.value;
                                                        if (selectedValue === -1) {
                                                          setOtherValue({
                                                            ...otherValue,
                                                            [index]: { isOther: true, value: '' },
                                                          });
                                                        } else {
                                                          setOtherValue({
                                                            ...otherValue,
                                                            [index]: { isOther: false, value: selectedValue },
                                                          });
                                                          handleRateChange(index, selectedValue);
                                                        }
                                                      }}
                                                      label="Importo"
                                                      value={otherValue[index]?.value || ''}
                                                    >
                                                      <MenuItem value={0}>0</MenuItem>
                                                      <MenuItem value={5}>5</MenuItem>
                                                      <MenuItem value={10}>10</MenuItem>
                                                      <MenuItem value={20}>20</MenuItem>
                                                      <MenuItem value={50}>50</MenuItem>
                                                      <MenuItem value={100}>100</MenuItem>
                                                      <MenuItem value={200}>200</MenuItem>
                                                      <MenuItem value={500}>500</MenuItem>
                                                      <MenuItem value={-1}>Altro</MenuItem>
                                                    </CustomTextField>
                                                  )
                                                ) : (
                                                  <Typography>
                                                    {isGdAvailable ? rate.price_discounted : rate.price}
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell align="center">{rate.currency}</TableCell>
                                              <TableCell align="center">
                                                {rate.has_editable === 1 ? (
                                                  <DefaultOutlinedButton
                                                    onClick={() => handleDisabledInput(index)}
                                                    className="rate-modify-button"
                                                  >
                                                    {disabledStates[index] ? 'Salva' : 'Modifica'}
                                                  </DefaultOutlinedButton>
                                                ) : (
                                                  <DefaultOutlinedButton
                                                    onClick={() => handleDisabledInput(index)}
                                                    disabled
                                                    className="rate-modify-button"
                                                  >
                                                    Fissa
                                                  </DefaultOutlinedButton>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                              Totale
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
                                              {!Number.isNaN(totalPrice) ? totalPrice.toFixed(2) : 'Valore non valido'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                              EUR
                                            </TableCell>
                                            <TableCell />
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>

                                    <FormControlLabel
                                      sx={{ paddingTop: '16px' }}
                                      control={
                                        <Switch
                                          checked={onlineMembershipCard}
                                          onChange={() => setOnlineMembershipCard(!onlineMembershipCard)}
                                        />
                                      }
                                      label={onlineMembershipCard ? 'Tessera Online' : 'Tessera Cartacea'}
                                    />
                                  </>
                                ) : (
                                  <Typography color="error">Nessuna tariffa disponibile</Typography>
                                )}
                              </Grid>
                            </Grid>
                          ) : isPaying && !paymentMethod.select ? (
                            // payment method selection card
                            <Grid container rowGap={2}>
                              {paymentTypes.map((type) => (
                                <Grid key={type.id} item xs={12}>
                                  <Card sx={{ display: 'flex' }}>
                                    <CardActionArea
                                      onClick={() => {
                                        membershipCard.payment_type = type.id;
                                        return setPaymentMethod({
                                          select: true,
                                          method: type.name,
                                          type: type.type,
                                        });
                                      }}
                                    >
                                      <CardContent sx={{ flex: '1 0 auto' }}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                          }}
                                        >
                                          <Typography component="div" variant="h5">
                                            {type.name}
                                          </Typography>
                                          {type.type === 'PCC' ? (
                                            <FontAwesomeIcon icon={faPaypal} size="2x" color="#003087" />
                                          ) : (
                                            <AccountBalanceIcon sx={{ height: 32, width: 32, color: '#009CDE' }} />
                                          )}
                                        </Box>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          ) : isPaying && paymentMethod.select && paymentMethod.type === 'PCC' ? (
                            // paypal chackout option
                            <PaypalCheckout
                              purchaseItems={orderItems}
                              setPaypalSuccess={setPaypalSuccess}
                              setIsPaying={setIsPaying}
                              paymentDone={onPaymentDone}
                            />
                          ) : isPaying && paymentMethod.select && paymentMethod.type === 'BB' ? (
                            // Bank transfer payment option

                            isBankDataLoading ? (
                              LoadingUI()
                            ) : (
                              <Grid container>
                                <List sx={{ width: '100%' }}>
                                  <ListItem>
                                    <Typography variant="subtitle1" component="div">
                                      Beneficiario: {beneficiary}
                                    </Typography>
                                  </ListItem>
                                  <Divider sx={{ marginLeft: 2 }} />

                                  <ListItem>
                                    <Typography variant="subtitle1" component="div">
                                      IBAN: {ibanCode}
                                    </Typography>
                                  </ListItem>
                                  <Divider sx={{ marginLeft: 2 }} />

                                  <ListItem>
                                    <Typography variant="subtitle1" component="div">
                                      Causale: {membershipCard.prefix}
                                      {membershipCard.card_number}
                                    </Typography>
                                  </ListItem>
                                  <Divider sx={{ marginLeft: 2 }} />

                                  <ListItem>
                                    <Typography variant="subtitle1" component="div">
                                      Importo: {getPrice(membershipCard.amount)} {membershipCard.currency}
                                    </Typography>
                                  </ListItem>
                                  <Divider sx={{ marginLeft: 2 }} />
                                </List>

                                <Typography sx={{ marginTop: 2 }} variant="caption" display="block">
                                  <InfoIcon fontSize="10px" />
                                  {paymentNote}
                                </Typography>
                              </Grid>
                            )
                          ) : (
                            // payment complete message
                            <Stack
                              direction="column"
                              justifyContent="center"
                              alignItems="center"
                              gap="25px"
                              sx={{ paddingY: 4, textAlign: 'center' }}
                            >
                              <CheckCircleOutlineIcon sx={{ fontSize: '5em', color: 'green' }} />
                              <Typography variant="h3">Pagamento andato buon fine!</Typography>
                            </Stack>
                          )}
                        </DialogContent>

                        <DialogActions
                          sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: '8px' }}
                        >
                          {paymentMethod.type === 'BB' && (
                            <Tooltip title="Copia dati" arrow placement="top">
                              <Box>
                                <CustomIconButton
                                  sx={{ height: '40px !important' }}
                                  onClick={() => {
                                    const copyText = navigator.clipboard.writeText(
                                      `Beneficiario: ${beneficiary}\nIBAN: ${ibanCode}\nCausale: Tesseramento PD - ${
                                        membershipCard.prefix
                                      }${membershipCard.card_number}\nImporto: ${getPrice(membershipCard.amount)} ${
                                        membershipCard.currency
                                      }`
                                    );
                                    setSnackbarData({
                                      text: 'Copiato con successo',
                                      color: 'info',
                                    });
                                    setOpenSnackbar(true);

                                    setTimeout(() => {
                                      setOpenSnackbar(false);
                                    }, POPUP_DURATION_DELAY);

                                    return copyText;
                                  }}
                                >
                                  <ContentCopy sx={{ height: '24px !important' }} />
                                </CustomIconButton>
                              </Box>
                            </Tooltip>
                          )}

                          {paymentMethod.type === 'BB' && (
                            <Tooltip title="Invia dati via mail" arrow placement="top">
                              <Box sx={{ marginLeft: '0 !important' }}>
                                <CustomIconButton onClick={sendBankDataEmail}>
                                  <EmailOutlinedIcon />
                                </CustomIconButton>
                              </Box>
                            </Tooltip>
                          )}

                          <DefaultOutlinedButton
                            onClick={() => {
                              return handleCloseNewCard();
                            }}
                          >
                            Chiudi
                          </DefaultOutlinedButton>
                          <DefaultContainedButton
                            customStyles={{ display: !isPaying && !paypalSuccess ? 'block' : 'none' }}
                            onClick={handleOpenCreateCardDialog}
                            disabled={!isSaveEnabled}
                          >
                            Crea
                          </DefaultContainedButton>
                          {isDialogOpen === 'newCard' && displayPopup()}
                        </DialogActions>
                      </form>
                    ) : subscriptionApiError?.length > 0 ? (
                      ErrorUI(subscriptionApiError)
                    ) : (
                      LoadingUI()
                    )}

                    {!subscriptionApiCalled && (
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                        {isDialogOpen === 'newCard' && displayPopup()}
                      </DialogActions>
                    )}
                  </Dialog>
                  <Dialog open={openCreateCardDialog} onClose={handleCloseCreateCardDialog}>
                    <DialogTitle>Sei sicuro di voler procedere al tesseramento?</DialogTitle>
                    <DialogContent>
                      <Typography>
                        Proseguendo la tariffa libera non sarà più modificabile e il contributo del tesseramento sarà
                        di:
                        {` ${totalPrice.toFixed(2)} €`}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <DefaultContainedButton onClick={onCreateNewMembershipCard}>Continua</DefaultContainedButton>
                      <DefaultOutlinedButton onClick={handleCloseCreateCardDialog}>Chiudi</DefaultOutlinedButton>
                      {isDialogOpen === 'createCard' && displayPopup()}
                    </DialogActions>
                  </Dialog>
                </Stack>

                {isBlacklistedEntity ? (
                  <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
                    Al momento il tesseramento è stato disabilitato.
                  </Alert>
                ) : (
                  <>
                  <Alert variant="filled" severity="error">
                    Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente, parlamentare)
                    inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere al tesseramento.{' '}
                  </Alert>

                  {hasDownloadableCard && userInfo.verified &&
                    <Alert severity="warning" sx={{ marginBottom: '8px', marginTop:'8px' }}>
                      Devi consentire i popup del browser per scaricare la tessera.
                    </Alert>}
                    
                  </>
                )}

                <Card sx={{ padding: '24px', marginTop: '10px' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell/>
                          <TableCell>Campagna</TableCell>
                          <TableCell>Stato Tessera</TableCell>
                          <TableCell>Data Tesseramento</TableCell>
                          <TableCell>Numero Tessera</TableCell>
                          <TableCell>Metodo di Pagamento</TableCell>
                          <TableCell>Quota Tessera</TableCell>
                          <TableCell>Stato Pagamento</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      {cards.length > 0 ? (
                        <TableBody>
                          {cards.map((card) => (
                            <TableRow key={card.id}>
                              <TableCell>
                              {Number(card.prefix) >= 2024 && (
                                  <Tooltip
                                    arrow
                                    title={
                                      card.payment_status?.type === 'P' && userInfo.verified
                                        ? 'Scarica tessera digitale'
                                        : 'Per scaricare la tessera digitale devi avere un account verificato e la tessera pagata'
                                    }
                                    placement="bottom"
                                  >
                                    <Box>
                                      <CustomIconButton
                                        sx={{
                                          color: '#212b36',
                                        }}
                                        disabled={card.payment_status?.type !== 'P' || !userInfo.verified}
                                        onClick={() => createDigitalCardPdf(card)}
                                      >
                                        <CreditCardIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                )}
                              </TableCell>

                              <TableCell>{card.campaign.description}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={dayjs(card.campaign.end_date) > dayjs(new Date()) ? '#007867' : '#b71d18'}
                                  bgColor={
                                    dayjs(card.campaign.end_date) > dayjs(new Date()) ? '#22c55e29' : '#ff563029'
                                  }
                                  text={dayjs(card.campaign.end_date) > dayjs(new Date()) ? 'Attiva' : 'Scaduta'}
                                />
                              </TableCell>
                              <TableCell>{dayjs(card.created_at).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>
                                {card.prefix}
                                {card.card_number}
                              </TableCell>
                              <TableCell>{card.payment_type ? card.payment_type.name : '//'}</TableCell>
                              <TableCell>{`${card.price?.toFixed(2)}€`}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={card.payment_status.type === 'P' ? '#007867' : '#b76e00'}
                                  bgColor={card.payment_status.type === 'P' ? '#22c55e29' : '#ffab0029'}
                                  text={card.payment_status.name}
                                />
                              </TableCell>
                              <TableCell>
                                {card.payment_status?.type !== 'P' && card.payment_type?.type !== 'CS' && (
                                  <Tooltip arrow placement="bottom" title="Procedi al pagamento">
                                    <Box>
                                      <CustomIconButton
                                        sx={{
                                          color: '#212b36',
                                        }}
                                        onClick={() => handleUserPaying(card)}
                                      >
                                        <ShoppingCart />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Typography variant="body2">Nessuna tessera disponibile</Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Card>
              </>
            ) : (
              ErrorUI(errorMessage)
            )}
          </ThripleTabPanel>

          {/* User position tab */}
          <ThripleTabPanel value={value} index={2}>
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                {positionTabValue === 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <DefaultContainedButton onClick={handleOpenNewPosition} startIcon={<AddIcon />}>
                      Aggiungi Carica
                    </DefaultContainedButton>

                    <NewPositionDialog
                      openDialog={openNewPosition}
                      closeDialog={handleCloseNewPosition}
                      userAuth={'user'}
                      userInfo={userInfo}
                      refreshFunction={handleNewPosition}
                    />
                  </Box>
                )}

                <Card sx={{ marginTop: '24px' }}>
                  {/* Filter Tabs */}
                  <Tabs
                    variant="fullWidth"
                    value={positionTabValue}
                    onChange={handleChangePositionTab}
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
                  >
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 0 ? '#000000' : '#edeff1',
                            borderRadius: '6px',
                            color: positionTabValue === 0 ? '#ffffff' : '#637381',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {externalPosition.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Istituzionali"
                    />
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 1 ? '#22c55e' : '#dbf6e5',
                            borderRadius: '6px',
                            color: positionTabValue === 1 ? '#ffffff' : '#118d57',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {internalPosition.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Politiche"
                    />
                  </Tabs>

                  <TableContainer sx={{ padding: '24px' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Stato Carica</TableCell>
                          <TableCell>Carica</TableCell>
                          {positionTabValue === 0 && <TableCell>Email Istituzionale</TableCell>}
                          {positionTabValue === 1 && <TableCell>Entità</TableCell>}
                          <TableCell>Data Inizio</TableCell>
                          <TableCell>Data Fine</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      {selectedPosition.length > 0 ? (
                        <TableBody>
                          <EditPositionDialog
                            openDialog={openEditPosition}
                            closeDialog={handleCloseEditPosition}
                            positionToEdit={positionToEdit}
                            setPositionToEdit={setPositionToEdit}
                            userAuth={'user'}
                            userInfo={userInfo}
                            refreshFunction={() => handleNewPosition()}
                          />

                          {/* Delete Position Dialog */}
                          <DecisionDialog
                            openDialog={openDeletePosition}
                            dialogTitle={'Elimina carica'}
                            dialogDescription={`Sei sicuro di voler eliminare la carica: ${positionToEdit.name} ?`}
                            closeOperation={closeDeletePositionDialog}
                            requestOperation={deletePosition}
                            requestText={'Elimina'}
                            closeText={'Chiudi'}
                            RequestButton={RedOutlinedButton}
                          />

                          {selectedPosition.map((position) => (
                            <TableRow key={position.id}>
                              <TableCell>{PositionStatusLabel(position, 'label')}</TableCell>
                              <TableCell>{position.name}</TableCell>
                              {position.internal === 0 && (
                                <TableCell>{position.email ? position.email : '/'}</TableCell>
                              )}

                              {position.internal === 1 && <TableCell>{position.entity_name}</TableCell>}
                              <TableCell>
                                {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '/'}
                              </TableCell>
                              <TableCell>
                                {position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '/'}
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="12px"
                                  maxWidth="150px"
                                >
                                  <Tooltip title="Modifica Carica" arrow placement="top">
                                    <Box>
                                      <CustomIconButton onClick={() => handleOpenEditPosition(position)}>
                                        <EditIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                  {position.internal === 0 && (
                                    <Tooltip title="Elimina Carica" arrow placement="top">
                                      <Box>
                                        <CustomIconButton onClick={() => openDeletePositionDialog(position)}>
                                          <DeleteIcon />
                                        </CustomIconButton>
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              Nessuna carica disponibile
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Card>
              </>
            ) : (
              ErrorUI(errorMessage)
            )}
          </ThripleTabPanel>
        </Box>

        {/* Popup message */}
        {isDialogOpen === 'noDialog' && (
          <Container ref={setAnchor}>
            <Popup anchor={anchor} open={open}>
              {popupSuccess ? (
                <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
              ) : (
                <PopupErrorBody>{popUpText}</PopupErrorBody>
              )}
            </Popup>
          </Container>
        )}
      </Container>

      {/* snackbar */}
      <Snackbar
        open={onOpenSnackbar}
        onClose={onSnackBarClose}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbarData.color} onClose={onSnackBarClose}>
          {snackbarData.text}
        </Alert>
      </Snackbar>
    </div>
  );
}